import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import userProfileService from '../../services/userProfileService';
import venueDashboardApiService from '../../services/venueDashboardApi';
import {
  bookingNeedsOrganizerPayment,
  canShowOrganizerBookingPricing,
} from '../../utils/bookingPayment';
import {
  getCachedUserBookings,
  setCachedUserBookings,
  getCachedBookingInvoice,
  setCachedBookingInvoice,
  clearProfileBookingsCache,
  clearBookingInvoiceCache
} from '../../utils/profileBookingsCache';
import {
  classifyBookingTab,
  formatOrganizerCurrency,
  formatOrganizerDate,
  formatOrganizerTimeRange,
  getOrganizerBookingBadge,
  getOrganizerPaymentLabel,
  ORGANIZER_BOOKING_TABS,
} from '../OrganizerDashboard/organizerUtils';
import { IconCalendar, IconUsers } from '../OrganizerDashboard/OrganizerIcons';
import '../OrganizerDashboard/OrganizerDashboard.css';

const resolveBackendUserId = (userProfile, currentUser) => {
  let backendUserId = null;

  if (userProfile?.backend_user_id) {
    backendUserId = userProfile.backend_user_id;
  } else if (userProfile?.firebase_uid) {
    backendUserId = userProfile.firebase_uid;
  } else if (currentUser?.uid) {
    backendUserId = currentUser.uid;
  }

  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser.id) {
        backendUserId = parsedUser.id;
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }

  return backendUserId;
};

const Bookings = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  // Set loading to false initially since we have sample data
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceActorId, setInvoiceActorId] = useState(null);
  const [invoiceByBooking, setInvoiceByBooking] = useState({});
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState('');
  const [venueImagesById, setVenueImagesById] = useState({});
  const [activeTab, setActiveTab] = useState(ORGANIZER_BOOKING_TABS.UPCOMING);

  const loadUserBookings = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      setError(null);

      const backendUserId = resolveBackendUserId(userProfile, currentUser);

      if (!backendUserId) {
        throw new Error('No backend user ID available for fetching bookings');
      }

      if (forceRefresh) {
        clearProfileBookingsCache(backendUserId);
        clearBookingInvoiceCache();
      } else {
        const cachedBookings = getCachedUserBookings(backendUserId);
        if (cachedBookings) {
          setInvoiceActorId(backendUserId || userProfile?.email || currentUser?.email || null);
          setBookings(cachedBookings);
          return;
        }
      }

      setBookingsLoading(true);

      setInvoiceActorId(backendUserId || userProfile?.email || currentUser?.email || null);
      userProfileService.setUser({ uid: backendUserId });
      const userBookings = await userProfileService.getUserBookings();
      const nextBookings = userBookings || [];
      setBookings(nextBookings);
      setCachedUserBookings(backendUserId, nextBookings);
    } catch (error) {
      setError('Failed to load booking data. Please refresh the page.');
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (currentUser) {
      const jwtToken = localStorage.getItem('access_token');

      if (jwtToken) {
        userProfileService.setAuthToken(jwtToken);
        userProfileService.setUser(currentUser);
        loadUserBookings();
      } else {
        setError('Authentication error. Please try logging in again.');
        setBookingsLoading(false);
      }
    } else {
      setBookingsLoading(false);
    }
  }, [currentUser, loadUserBookings]);

  useEffect(() => {
    const loadBookingInvoices = async () => {
      if (!invoiceActorId || bookings.length === 0) {
        setInvoiceByBooking({});
        return;
      }

      const pairs = await Promise.allSettled(
        bookings.map(async (booking) => {
          const bookingId = booking.id || booking.booking_reference;
          if (!bookingId) return null;

          const cachedInvoice = getCachedBookingInvoice(bookingId);
          if (cachedInvoice) {
            return [bookingId, cachedInvoice];
          }

          const files = await venueDashboardApiService.listBookingOwnerInvoices(bookingId, {
            userId: invoiceActorId
          });
          const readyFile = files.find((file) => String(file?.status || '').toLowerCase() === 'ready') || files[0];
          if (!readyFile?.id) return null;
          const meta = {
            fileId: readyFile.id,
            name: readyFile.filename || readyFile.name || 'Invoice'
          };
          setCachedBookingInvoice(bookingId, meta);
          return [bookingId, meta];
        })
      );

      const nextMap = {};
      pairs.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const [bookingId, meta] = result.value;
        if (bookingId && meta) nextMap[bookingId] = meta;
      });
      setInvoiceByBooking(nextMap);
    };

    loadBookingInvoices();
  }, [bookings, invoiceActorId]);

  useEffect(() => {
    const loadVenueImages = async () => {
      const venueIds = [...new Set(
        bookings
          .map((booking) => booking.venueId)
          .filter((id) => typeof id === 'string' && id.trim())
      )];

      if (venueIds.length === 0) {
        setVenueImagesById({});
        return;
      }

      const results = await Promise.allSettled(
        venueIds.map(async (venueId) => {
          const venue = await venueDashboardApiService.getVenueDetails(venueId);
          const imageUrl =
            venue?.primary_image_url ||
            venue?.primaryImageUrl ||
            (Array.isArray(venue?.images)
              ? (venue.images.find((img) => img?.is_primary)?.image_url || venue.images[0]?.image_url)
              : null) ||
            venue?.image_url ||
            venue?.image ||
            '';

          return [venueId, imageUrl || ''];
        })
      );

      const nextMap = {};
      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const [venueId, imageUrl] = result.value;
        if (venueId && imageUrl) {
          nextMap[venueId] = imageUrl;
        }
      });

      setVenueImagesById(nextMap);
    };

    loadVenueImages();
  }, [bookings]);

  const handleDownloadInvoice = async (bookingId, invoiceFileId) => {
    if (!bookingId || !invoiceFileId || !invoiceActorId) return;
    try {
      setDownloadingInvoiceId(bookingId);
      const response = await venueDashboardApiService.getBookingOwnerInvoiceDownloadUrl(bookingId, invoiceFileId, {
        userId: invoiceActorId
      });
      if (response?.download_url) {
        window.open(response.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (downloadError) {
      setError('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoiceId('');
    }
  };

  const getInvoiceMetaFromBooking = (booking) => {
    const req = booking?.requirements || {};
    const invoiceCandidate =
      booking?.invoice ||
      req?.invoice ||
      booking?.invoice_file ||
      req?.invoice_file ||
      null;

    const url =
      booking?.invoice_url ||
      req?.invoice_url ||
      invoiceCandidate?.url ||
      invoiceCandidate?.file_url ||
      null;

    const name =
      booking?.invoice_file_name ||
      req?.invoice_file_name ||
      invoiceCandidate?.name ||
      invoiceCandidate?.file_name ||
      (typeof url === 'string' ? url.split('/').pop() : null) ||
      null;

    if (!url && !name) return null;
    return { url: url || null, name: name || 'Invoice' };
  };

  const tabCounts = useMemo(() => {
    const counts = {
      [ORGANIZER_BOOKING_TABS.UPCOMING]: 0,
      [ORGANIZER_BOOKING_TABS.COMPLETED]: 0,
      [ORGANIZER_BOOKING_TABS.CANCELLED]: 0,
    };
    bookings.forEach((booking) => {
      counts[classifyBookingTab(booking)] += 1;
    });
    return counts;
  }, [bookings]);

  const filteredBookings = useMemo(
    () => bookings.filter((booking) => classifyBookingTab(booking) === activeTab),
    [bookings, activeTab]
  );

  const tabLabels = {
    [ORGANIZER_BOOKING_TABS.UPCOMING]: 'Upcoming',
    [ORGANIZER_BOOKING_TABS.COMPLETED]: 'Completed',
    [ORGANIZER_BOOKING_TABS.CANCELLED]: 'Cancelled',
  };

  return (
    <div className="org-bookings">
      <h1 className="org-page-title">Bookings</h1>

      {error && (
        <div className="org-message org-message--error">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="org-bookings-tabs" role="tablist" aria-label="Booking filters">
        {Object.values(ORGANIZER_BOOKING_TABS).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`org-bookings-tab ${activeTab === tab ? 'org-bookings-tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tabLabels[tab]} ({tabCounts[tab]})
          </button>
        ))}
      </div>

      {bookingsLoading ? (
        <div className="org-loading">
          <div className="org-loading-spinner" />
          <p>Loading your bookings...</p>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="org-empty">
          <h3>No {tabLabels[activeTab].toLowerCase()} bookings</h3>
          <p>
            {activeTab === ORGANIZER_BOOKING_TABS.UPCOMING
              ? 'Start exploring venues and make your first booking!'
              : `You don't have any ${tabLabels[activeTab].toLowerCase()} bookings yet.`}
          </p>
          {activeTab === ORGANIZER_BOOKING_TABS.UPCOMING ? (
            <button type="button" className="org-btn org-btn--primary" onClick={() => navigate('/search-results')}>
              Discover venues
            </button>
          ) : null}
        </div>
      ) : (
        <div className="org-bookings-list">
          {filteredBookings.map((booking) => {
            const badge = getOrganizerBookingBadge(booking);
            const bookingId = booking.id || booking.booking_reference;
            const apiInvoiceMeta = bookingId ? invoiceByBooking[bookingId] : null;
            const invoiceMeta = apiInvoiceMeta || getInvoiceMetaFromBooking(booking);
            const bookingImage = booking.venueImage || venueImagesById[booking.venueId] || '';
            const venueApproved = canShowOrganizerBookingPricing(booking);
            const paymentLabel = getOrganizerPaymentLabel(booking, venueApproved);

            return (
              <article
                key={bookingId || `${booking.venueId || 'venue'}-${booking.date || 'date'}`}
                className="org-booking-card"
              >
                {bookingImage ? (
                  <img src={bookingImage} alt={booking.venueName || 'Venue'} className="org-booking-card-image" />
                ) : (
                  <div className="org-booking-card-image org-booking-card-image--placeholder" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}

                <div className="org-booking-card-body">
                  <div className="org-booking-card-header">
                    <h3 className="org-booking-card-venue">{booking.venueName || 'Venue'}</h3>
                    <span className={`org-badge org-badge--${badge.variant}`}>
                      {badge.label}
                    </span>
                  </div>

                  <div className="org-home-meta-row">
                    <IconUsers />
                    <span>{booking.headcount || booking.attendees || '—'} guests</span>
                  </div>
                  <div className="org-home-meta-row">
                    <IconCalendar />
                    <span>
                      {booking.bookingFrequency || booking.frequency || 'Single session'}
                    </span>
                    <span className="org-home-meta-sep">/</span>
                    <span>{formatOrganizerDate(booking.date || booking.bookingDate)}</span>
                  </div>
                  <div className="org-home-meta-row">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 8V13L15 15M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{formatOrganizerTimeRange(booking.startTime, booking.endTime)}</span>
                    {booking.duration ? (
                      <>
                        <span className="org-home-meta-sep">/</span>
                        <span>{booking.duration} hours</span>
                      </>
                    ) : null}
                  </div>

                  <div className="org-booking-card-footer">
                    <div>
                      <div className="org-booking-card-total-value">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M8 2V5M16 2V5M7 11H17M7 15H13M7 3.5H17C18.933 3.5 20.5 5.067 20.5 7V17C20.5 18.933 18.933 20.5 17 20.5H7C5.067 20.5 3.5 18.933 3.5 17V7C3.5 5.067 5.067 3.5 7 3.5Z" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                        <span>Booking total:</span>
                        <strong>
                          {venueApproved
                            ? formatOrganizerCurrency(booking.totalPrice || booking.amountPending)
                            : 'TBC'}
                        </strong>
                      </div>
                    </div>

                    <div className="org-booking-card-actions">
                      <span className={`org-payment-pill org-payment-pill--${paymentLabel.variant}`}>
                        {paymentLabel.label}
                      </span>
                      {bookingId && bookingNeedsOrganizerPayment(booking) ? (
                        <Link to={`/profile/bookings/${bookingId}?pay=stripe`} className="org-btn org-btn--primary org-btn--compact">
                          Pay now
                        </Link>
                      ) : null}
                      {bookingId && !bookingNeedsOrganizerPayment(booking) ? (
                        <Link to={`/profile/bookings/${bookingId}`} className="org-booking-card-link">
                          View details
                        </Link>
                      ) : null}
                      {bookingId && venueApproved && invoiceMeta && !bookingNeedsOrganizerPayment(booking) ? (
                        invoiceMeta.fileId ? (
                          <button
                            type="button"
                            className="org-booking-card-link"
                            onClick={() => handleDownloadInvoice(bookingId, invoiceMeta.fileId)}
                            disabled={downloadingInvoiceId === bookingId}
                          >
                            {downloadingInvoiceId === bookingId ? 'Preparing...' : 'Invoice'}
                          </button>
                        ) : invoiceMeta.url ? (
                          <a className="org-booking-card-link" href={invoiceMeta.url} target="_blank" rel="noreferrer">
                            Invoice
                          </a>
                        ) : null
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Bookings;

